import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, serverTimestamp, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './SessionManagement.css';

const SessionManagement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedSession, setSelectedSession] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, [sortBy, sortOrder]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      let sessionsQuery;

      if (sortBy === 'createdAt') {
        sessionsQuery = query(
          collection(db, 'sessions'),
          orderBy(sortBy, sortOrder)
        );
      } else {
        sessionsQuery = collection(db, 'sessions');
      }

      const sessionsSnapshot = await getDocs(sessionsQuery);
      let sessionsData = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by other fields if needed
      if (sortBy !== 'createdAt') {
        sessionsData.sort((a, b) => {
          const aValue = a[sortBy] || '';
          const bValue = b[sortBy] || '';
          
          if (sortOrder === 'asc') {
            return aValue.toString().localeCompare(bValue.toString());
          } else {
            return bValue.toString().localeCompare(aValue.toString());
          }
        });
      }

      setSessions(sessionsData);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredSessions = () => {
    let filtered = sessions;

    // Filter by status
    if (filter !== 'all') {
      filtered = filtered.filter(session => session.status === filter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(session =>
        session.practitionerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.providerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.sessionType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        session.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const handleStatusUpdate = async (sessionId, newStatus) => {
    try {
      await updateDoc(doc(db, 'sessions', sessionId), {
        status: newStatus,
        updatedAt: serverTimestamp()
      });

      setSessions(prev => prev.map(session =>
        session.id === sessionId
          ? { ...session, status: newStatus }
          : session
      ));
    } catch (error) {
      console.error('Error updating session status:', error);
      alert('Error updating session status');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this session? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'sessions', sessionId));
      setSessions(prev => prev.filter(session => session.id !== sessionId));
      setShowModal(false);
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Error deleting session');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  };

  const formatScheduledDateTime = (date, time) => {
    if (!date || !time) return 'Not scheduled';
    
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    const formattedTime = `${hour12}:${minutes} ${ampm}`;
    
    return `${new Date(date).toLocaleDateString()} at ${formattedTime}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return '#f59e0b';
      case 'confirmed': return '#3b82f6';
      case 'in_progress': return '#059669';
      case 'completed': return '#6b7280';
      case 'cancelled': return '#dc2626';
      case 'expired': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const openSessionModal = (session) => {
    setSelectedSession(session);
    setShowModal(true);
  };

  if (loading) {
    return <div className="loading">Loading sessions...</div>;
  }

  return (
    <div className="session-management">
      <div className="admin-header">
        <div className="admin-breadcrumb">
          <button onClick={() => navigate('/admin/dashboard')} className="breadcrumb-link">
            ← Back to Admin Dashboard
          </button>
        </div>
        <h1>Session Management</h1>
        <p>Manage and monitor all practice sessions</p>
      </div>

      <div className="admin-controls">
        <div className="control-group">
          <label>Filter by Status:</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">All Sessions</option>
            <option value="scheduled">Scheduled</option>
            <option value="confirmed">Confirmed</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="expired">Expired</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        <div className="control-group">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search by practitioner, provider, session type, or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="control-group">
          <label>Sort by:</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="createdAt">Created Date</option>
            <option value="scheduledDate">Scheduled Date</option>
            <option value="status">Status</option>
            <option value="amount">Amount</option>
          </select>
        </div>

        <div className="control-group">
          <label>Order:</label>
          <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}>
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>
      </div>

      <div className="sessions-stats">
        <div className="stat-card">
          <h3>Total Sessions</h3>
          <p>{sessions.length}</p>
        </div>
        <div className="stat-card">
          <h3>Filtered Results</h3>
          <p>{getFilteredSessions().length}</p>
        </div>
        <div className="stat-card">
          <h3>Completed Sessions</h3>
          <p>{sessions.filter(s => s.status === 'completed').length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <p>{formatCurrency(sessions.filter(s => s.status === 'completed').reduce((sum, s) => sum + (s.amount || 0), 0))}</p>
        </div>
      </div>

      <div className="sessions-table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th>Session ID</th>
              <th>Practitioner</th>
              <th>Provider</th>
              <th>Type</th>
              <th>Status</th>
              <th>Scheduled</th>
              <th>Amount</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getFilteredSessions().map(session => (
              <tr key={session.id}>
                <td>
                  <span className="session-id" title={session.id}>
                    {session.id.substring(0, 8)}...
                  </span>
                </td>
                <td>{session.practitionerName || 'N/A'}</td>
                <td>{session.providerName || 'N/A'}</td>
                <td>{session.sessionType || 'N/A'}</td>
                <td>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(session.status) }}
                  >
                    {session.status}
                  </span>
                </td>
                <td>{formatScheduledDateTime(session.scheduledDate, session.scheduledTime)}</td>
                <td>{formatCurrency(session.amount || 0)}</td>
                <td>{formatDate(session.createdAt)}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => openSessionModal(session)}
                      className="btn-view"
                    >
                      View
                    </button>
                    <select
                      value={session.status}
                      onChange={(e) => handleStatusUpdate(session.id, e.target.value)}
                      className="status-select"
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="expired">Expired</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {getFilteredSessions().length === 0 && (
          <div className="no-results">
            <p>No sessions found matching your criteria.</p>
          </div>
        )}
      </div>

      {showModal && selectedSession && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Session Details</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="session-details-grid">
                <div className="detail-row">
                  <strong>Session ID:</strong>
                  <span>{selectedSession.id}</span>
                </div>
                <div className="detail-row">
                  <strong>Practitioner:</strong>
                  <span>{selectedSession.practitionerName}</span>
                </div>
                <div className="detail-row">
                  <strong>Provider:</strong>
                  <span>{selectedSession.providerName}</span>
                </div>
                <div className="detail-row">
                  <strong>Session Type:</strong>
                  <span>{selectedSession.sessionType}</span>
                </div>
                <div className="detail-row">
                  <strong>Status:</strong>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(selectedSession.status) }}
                  >
                    {selectedSession.status}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Scheduled:</strong>
                  <span>{formatScheduledDateTime(selectedSession.scheduledDate, selectedSession.scheduledTime)}</span>
                </div>
                <div className="detail-row">
                  <strong>Duration:</strong>
                  <span>{selectedSession.duration} minutes</span>
                </div>
                <div className="detail-row">
                  <strong>Amount:</strong>
                  <span>{formatCurrency(selectedSession.amount || 0)}</span>
                </div>
                <div className="detail-row">
                  <strong>Created:</strong>
                  <span>{formatDate(selectedSession.createdAt)}</span>
                </div>
                <div className="detail-row">
                  <strong>Updated:</strong>
                  <span>{formatDate(selectedSession.updatedAt)}</span>
                </div>
                {selectedSession.notes && (
                  <div className="detail-row">
                    <strong>Notes:</strong>
                    <span>{selectedSession.notes}</span>
                  </div>
                )}
                {selectedSession.paymentIntentId && (
                  <div className="detail-row">
                    <strong>Payment Intent:</strong>
                    <span>{selectedSession.paymentIntentId}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => handleDeleteSession(selectedSession.id)}
                className="btn-delete"
              >
                Delete Session
              </button>
              <button onClick={() => setShowModal(false)} className="btn-cancel">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionManagement;